import { Box, Skeleton, Theme } from "@outschool/backpack";
import {
  ActivityListing_ListingFragment as Listing,
  SearchListingsQuery,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useProductListViewed } from "@outschool/ui-analytics";
import { ApolloError } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Container, Loading } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useState } from "react";

import ErrorMessage from "../../ErrorMessage";
import { ActivityListing } from "../../listings/ActivityListing";
import TooSpecific from "../../search/TooSpecific";
import CardGrid from "../CardGrid";

type Listings = SearchListingsQuery["searchListings"]["listings"];

type CategoryListingsProps = {
  categorySlug: string;
  error?: ApolloError;
  exactTotal: number;
  fetchMore: () => void;
  isFetchingMore: boolean;
  listings: Listings;
  loading: boolean;
  searchUid: string | undefined;
  variant?: string;
  ListingFilters?: React.ReactElement;
  ListingEmbeddings?: {
    positionFn: (index: number) => boolean;
    Component: JSX.Element;
  }[];
  isReadOnlyMode: boolean;
  isTutoringCLP?: boolean;
  hideLoadMoreButton?: boolean;
};

const CategoryListings: React.FC<CategoryListingsProps> = ({
  categorySlug,
  error,
  exactTotal,
  fetchMore,
  isFetchingMore,
  listings,
  loading,
  searchUid,
  variant,
  ListingFilters = null,
  ListingEmbeddings = [],
  isReadOnlyMode,
  isTutoringCLP,
  hideLoadMoreButton = false,
}) => {
  const { t } = useTranslation(
    "client\\components\\categories\\CategoryListings",
  );
  const trackProductListViewed = useProductListViewed();
  const isMobile = useIsMobile();
  const { isLoggedIn } = useSession();
  const [index, setIndex] = React.useState(0);
  const [page, setPage] = useState(1);
  const isLoading =
    loading || isFetchingMore || (exactTotal === null && !error);
  const canLoadMore =
    !isLoading && exactTotal !== listings.length && !hideLoadMoreButton;
  const isTooSpecific = !isLoading && !listings?.length;

  const fetchMoreActivities = () => {
    setPage(prev => prev + 1);
    fetchMore();
  };

  React.useEffect(() => {
    if (index === listings.length) {
      return;
    }

    trackProductListViewed(
      "category-page",
      `page-${page}`,
      // @ts-ignore TS(2345): Argument of type 'ActivityListing_ListingFragment[... Remove this comment to see the full error message
      listings.slice(index, listings.length),
    );
    setIndex(listings.length);
  }, [index, listings, page, trackProductListViewed]);

  return (
    <Container fullBleed={isMobile}>
      {ListingFilters}
      <Box
        data-test-id="category-listings"
        flex
        sx={{
          paddingTop: "16px",
          flexDirection: "column",
        }}
      >
        {listings.length > 0 ? (
          <>
            {isTutoringCLP ? (
              <Box sx={{ margin: "0px" }}>
                <CardGrid
                  listings={listings}
                  trackingLabel="category_listing_desktop"
                  trackingProperties={{
                    searchUid,
                    category_slug: categorySlug,
                  }}
                  variant={variant}
                  rowCount={Math.floor(listings.length / 4)}
                  isTutoringCLP={true}
                />
              </Box>
            ) : (
              listings.map((listing, activity_index) => {
                return (
                  <React.Fragment key={listing.activity.uid}>
                    {/* @ts-ignore TS(2322): Type '{ listing: ActivityListing_ListingFragment; ... Remove this comment to see the full error message */}
                    <BasicPageListing
                      {...{
                        listing,
                        activityIndex: activity_index,
                        isLoggedIn,
                        searchUid,
                        categorySlug,
                        variant,
                      }}
                    />
                    {ListingEmbeddings.filter(le =>
                      le.positionFn(activity_index),
                    ).map(le => {
                      return (
                        <React.Fragment
                          key={activity_index + "ListingEmbeddings"}
                        >
                          {le.Component}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </>
        ) : error ? (
          <Box sx={{ padding: "10" }}>
            <ErrorMessage value={error} showStatusPageLink={isReadOnlyMode} />
          </Box>
        ) : isTooSpecific ? (
          <TooSpecific />
        ) : null}
        {isLoading && !listings.length && (
          <Box>
            <Skeleton
              variant="rectangular"
              height={"203px"}
              sx={{ my: "1em", mx: ".5em", borderRadius: "10px" }}
            />
            <Skeleton
              variant="rectangular"
              height={"203px"}
              sx={{ my: "1em", mx: ".5em", borderRadius: "10px" }}
            />
            <Skeleton
              variant="rectangular"
              height={"203px"}
              sx={{ my: "1em", mx: ".5em", borderRadius: "10px" }}
            />
            <Skeleton
              variant="rectangular"
              height={"203px"}
              sx={{ my: "1em", mx: ".5em", borderRadius: "10px" }}
            />
            <Skeleton
              variant="rectangular"
              height={"203px"}
              sx={{ my: "1em", mx: ".5em", borderRadius: "10px" }}
            />
          </Box>
        )}
        {isLoading && listings.length > 0 && <Loading />}
        {canLoadMore && (
          <TrackedButton
            variant="contained"
            sx={(theme: Theme) => ({
              alignSelf: "center",
              marginTop: "39px",

              [theme.breakpoints.up("lg")]: {
                marginTop: "57px",
              },
            })}
            onClick={fetchMoreActivities}
            trackingName="category_listings_load_more"
            trackingSharedProperties={{
              category_page: categorySlug,
              page_index: page,
            }}
          >
            {t("View More Classes")}
          </TrackedButton>
        )}
      </Box>
    </Container>
  );
};

/**
 *
 * Encapsulates logic for which component to use to display listings especifically for the basic category page
 *
 */
const BasicPageListing: React.FC<{
  listing: Listing;

  /** Index of the listing in the listings array (0-indexed) */
  activityIndex: number;
  searchUid: string;
  categorySlug: string;
}> = ({ listing, activityIndex, searchUid, categorySlug }) => {
  return (
    <ActivityListing
      listing={listing}
      openInNewTab
      trackingProperties={{
        searchUid,
        order: activityIndex + 1, // We want 1-indexed order for tracking.
        activity_title: listing.activity.title,
        category_page: categorySlug,
      }}
      listingIndex={activityIndex}
    />
  );
};

export default CategoryListings;
